import { useState } from 'react';
import {
  getSectionRevisions,
  setTenderSectionSuggestion,
} from '../../../services/tenderSectionService';
import type { Section, SectionRevision } from '../../../types/tender';

type HistoryState = {
  past: { content: string; suggested: string | undefined }[];
  future: { content: string; suggested: string | undefined }[];
};

export function useSectionHistory(
  tenderId: string,
  saveContent: (section: Section, content: string) => Promise<boolean>,
  resolveSuggestion: (section: Section, accept: boolean, finalContent?: string) => Promise<void>,
  updateSectionState: (sectionId: string, updates: Partial<Section>) => void,
  editingSectionId: string | null,
  setEditContent: (content: string) => void,
  setShowDiffs: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  setExpandedSectionIds: React.Dispatch<React.SetStateAction<Set<string>>>,
  showSnackbar: (msg: string, severity?: 'success' | 'info' | 'warning' | 'error') => void,
  unlock: (section: Section) => Promise<void>
) {
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyRevisions, setHistoryRevisions] = useState<SectionRevision[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sectionForHistory, setSectionForHistory] = useState<Section | null>(null);
  const [historyComparisonSectionId, setHistoryComparisonSectionId] = useState<string | null>(null);
  const [comparisonData, setComparisonData] = useState<{
    sectionId: string;
    content: string;
    suggested_content: string;
  } | null>(null);

  const [history, setHistory] = useState<Record<string, HistoryState>>({});

  const pushToHistory = (sectionId: string, currentContent: string, currentSuggested?: string) => {
    setHistory((prev) => {
      const currentHistory = prev[sectionId] || { past: [], future: [] };
      return {
        ...prev,
        [sectionId]: {
          past: [...currentHistory.past, { content: currentContent, suggested: currentSuggested }],
          future: [],
        },
      };
    });
  };

  const handleOpenHistory = async (section: Section) => {
    setSectionForHistory(section);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    try {
      const revisions = await getSectionRevisions(tenderId, section.id);
      setHistoryRevisions(revisions);
    } catch (_err) {
      showSnackbar('Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSelectRevision = async (revision: SectionRevision) => {
    if (!sectionForHistory) {
      return;
    }

    setHistoryDialogOpen(false);
    try {
      if (revision.encrypted_content) {
        const currentContent = sectionForHistory.content;
        // Legacy fields might be called encrypted_content but it is base64 plain text now.
        let revisionContent = revision.encrypted_content;
        try {
          revisionContent = atob(revisionContent);
        } catch {
          // If it fails to decode, it might not be base64.
        }

        setComparisonData({
          sectionId: sectionForHistory.id,
          content: currentContent,
          suggested_content: revisionContent,
        });

        setHistoryComparisonSectionId(sectionForHistory.id);
        setShowDiffs((prev) => ({ ...prev, [sectionForHistory.id]: true }));
        setExpandedSectionIds((prev) => {
          const next = new Set(prev);
          next.add(sectionForHistory.id);
          return next;
        });
        showSnackbar('Comparing Current (Left) vs Revision (Right).', 'info');
      }
    } catch (_e) {
      showSnackbar('Failed to decrypt revision', 'error');
    }
  };

  const handleUndo = async (section: Section) => {
    const sectionHistory = history[section.id] || { past: [], future: [] };
    if (sectionHistory.past.length === 0) {
      return;
    }

    const previous = sectionHistory.past[sectionHistory.past.length - 1];
    const newPast = sectionHistory.past.slice(0, -1);
    const newFuture = [
      { content: section.content, suggested: section.suggested_content },
      ...sectionHistory.future,
    ];

    if (editingSectionId === section.id && previous) {
      setEditContent(previous.content);
    }

    if (previous) {
      await Promise.all([
        saveContent(section, previous.content),
        previous.suggested !== undefined
          ? setTenderSectionSuggestion(tenderId, section.id, previous.suggested)
          : resolveSuggestion(section, false),
      ]);
      updateSectionState(section.id, {
        content: previous.content,
        suggested_content: previous.suggested,
      });

      setHistory((prev) => ({
        ...prev,
        [section.id]: { past: newPast, future: newFuture },
      }));
      showSnackbar('Undid change.', 'info');
    }
  };

  const handleRedo = async (section: Section) => {
    const sectionHistory = history[section.id] || { past: [], future: [] };
    if (sectionHistory.future.length === 0) {
      return;
    }

    const next = sectionHistory.future[0];
    const newFuture = sectionHistory.future.slice(1);
    const newPast = [
      ...sectionHistory.past,
      { content: section.content, suggested: section.suggested_content },
    ];

    if (editingSectionId === section.id && next) {
      setEditContent(next.content);
    }

    if (next) {
      await Promise.all([
        saveContent(section, next.content),
        next.suggested !== undefined
          ? setTenderSectionSuggestion(tenderId, section.id, next.suggested)
          : resolveSuggestion(section, false),
      ]);
      updateSectionState(section.id, {
        content: next.content,
        suggested_content: next.suggested,
      });

      setHistory((prev) => ({
        ...prev,
        [section.id]: { past: newPast, future: newFuture },
      }));
      showSnackbar('Redid change.', 'info');
    }
  };

  const handleRestoreRevision = async (section: Section) => {
    if (!section.suggested_content) {
      return;
    }
    await saveContent(section, section.suggested_content);
    setHistoryComparisonSectionId(null);
    setComparisonData(null);
    await unlock(section);
    showSnackbar('Revision restored successfully.', 'success');
  };

  const handleCancelHistoryComparison = async (section: Section) => {
    setHistoryComparisonSectionId(null);
    setComparisonData(null);
    await unlock(section);
  };

  return {
    historyDialogOpen,
    setHistoryDialogOpen,
    historyRevisions,
    historyLoading,
    sectionForHistory,
    historyComparisonSectionId,
    comparisonData,
    handleOpenHistory,
    handleSelectRevision,
    handleUndo,
    handleRedo,
    handleRestoreRevision,
    handleCancelHistoryComparison,
    pushToHistory,
  };
}
